import React, { useEffect, useState } from 'react';
import { DossierOpenSearch, useFormaliteService } from '@europrocurement/l2d-domain';
import { DossierApiApiDossiersIdDeplacerPatchRequest } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { DocumentsSelectorsForm } from '@europrocurement/l2d-modules';
import { DisplayedDocument } from '@europrocurement/l2d-modules/modules/Dossiers/providers/types';
import { castIriToId } from '@europrocurement/l2d-utils';
import { OptionsType, useModal } from '@europrocurement/flexy-components';
import { useSnackbar } from 'notistack';
import { useHandleError } from '@europrocurement/l2d-hooks';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import { FieldValues, useForm } from 'react-hook-form';

export type MoveFormalitiesToOtherDossierFormProps = {
    formalitiesIds: number[];
    dossier: DossierOpenSearch;
    documents: DisplayedDocument[];
    onSubmitDone?: (dossiedId?: number) => void;
    options: OptionsType;
};
const MoveFormalitiesToOtherDossierForm: React.FunctionComponent<MoveFormalitiesToOtherDossierFormProps> =
    function ({ formalitiesIds, dossier, documents, onSubmitDone = () => {}, options }) {
        const { formaliteApi, dossierApi } = useFormaliteService();
        const { palette } = useTheme();
        const { modalActions } = useModal();
        const { enqueueSnackbar } = useSnackbar();
        const { apiErrorMessageSnackbar } = useHandleError();
        const [documentsIdsFormality, setDocumentsIdsFormality] = useState<number[]>([]);
        const [selectedDocuments, setSelectedDocuments] = useState<DisplayedDocument[]>([]);
        const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
        const [dossiersOptions, setDossiersOptions] = useState<OptionsType>([]);
        const [formStructure, setFormStructure] = useState<FormStructure[]>([]);
        const formContext = useForm<FieldValues>({
            mode: 'onTouched',
            defaultValues: { selectDossier: options[0] },
        });

        // hydrate initals states
        useEffect(() => {
            if (!dossier.id) {
                return;
            }

            setDocumentsIdsFormality([]);
            // get formalities from ids props and retrieve documents linked
            formalitiesIds.map((formalityId) => {
                formaliteApi
                    .apiFormalitesIdGet({ id: formalityId.toString() })
                    .then((res) => {
                        const documentsIds = res.data.documents.map((document) =>
                            castIriToId(document),
                        );

                        setDocumentsIdsFormality((state) => state.concat(documentsIds));
                    })
                    .catch((err) => {
                        enqueueSnackbar(
                            'Une erreur est survenue lors de la récupération des documents liés à la prestation',
                            { variant: 'error' },
                        );
                        console.log(err.message);
                    });

                return true;
            });

            const formStr: FormStructure[] = [
                {
                    type: 'select',
                    name: 'selectDossier',
                    inputlabel: 'Dossier',
                    options,
                    xs: 12,
                    defaultValue: options[0],
                },
            ];
            setDossiersOptions(options);
            setFormStructure(formStr);

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const handleMoveFormalityInOtherDossier = () => {
            // To check: validator ongoing ?
            if (!dossier.id) {
                return;
            }
            const { selectDossier } = formContext.getValues();
            if (!selectDossier) {
                enqueueSnackbar(
                    'Une erreur est survenue lors de la récupération du dossier sélectionné',
                );
                return;
            }
            setIsSubmitting(true);

            const prestationsIds: number[] = [];
            dossier.formalites.map((formality) => {
                if (formalitiesIds.includes(formality.id)) {
                    formality.prestationId.map((prestationId) => prestationsIds.push(prestationId));
                }
                return true;
            });

            const documentsIds = selectedDocuments.map((doc) => doc.id);

            const request: DossierApiApiDossiersIdDeplacerPatchRequest = {
                id: dossier.id.toString(),
                apiDossiersIdDeplacerPatchRequest: {
                    documentsIds,
                    prestationsIds,
                    dossierId: selectDossier.value as number,
                },
            };
            dossierApi
                .apiDossiersIdDeplacerPatch(request)
                .then(() => {
                    if (prestationsIds.length > 1) {
                        enqueueSnackbar('Les prestations ont bien été déplacées');
                    } else {
                        enqueueSnackbar('La prestation a bien été déplacée');
                    }

                    modalActions.close();
                })
                .catch((err) => {
                    apiErrorMessageSnackbar(
                        err,
                        'Une erreur est survenue lors du déplacement de formalité',
                    );
                    console.log(err.message);
                })
                .finally(() => {
                    setIsSubmitting(false);
                    onSubmitDone(selectDossier.value);
                });
        };

        const formProps = {
            formContext,
            onSubmit: () => {},
            submitButton: {
                displayed: false,
            },
        };

        return (
            <Box sx={{ width: '550px', gap: '24px', display: 'grid' }}>
                <Typography variant="l2dh6">Déplacer dans un dossier</Typography>

                {/* Select dossier */}
                {dossiersOptions && (
                    <FlexyForm
                        formStructure={formStructure}
                        {...formProps}
                    />
                )}

                {documentsIdsFormality && (
                    <Box sx={{ display: 'grid', gap: '14px' }}>
                        <Typography variant="l2dcaption">Documents à dupliquer</Typography>

                        <DocumentsSelectorsForm
                            documents={documents}
                            preSelectedDocumentsIds={documentsIdsFormality}
                            setSelectedDocuments={setSelectedDocuments}
                            sxWrapper={{ maxHeight: '35vh' }}
                        />
                    </Box>
                )}

                {/* Footer */}
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'end', gap: '16px' }}>
                        <Button
                            variant="text"
                            sx={{
                                padding: '16px 24px',
                                alignItems: 'center',
                                color: palette.primary.main,
                            }}
                            onClick={() => modalActions.close()}
                        >
                            Annuler
                        </Button>
                        <Button
                            disabled={isSubmitting}
                            onClick={handleMoveFormalityInOtherDossier}
                            sx={{
                                padding: '8px 20px',
                                fontSize: '16px',
                                background: palette.primary.main,
                                ':hover': {
                                    background: palette.primary.light,
                                },
                                transition: 'all ease-in-out 300ms',
                            }}
                            variant="contained"
                            key="publish-bodacc-btn"
                        >
                            {isSubmitting ? (
                                <CircularProgress
                                    color="inherit"
                                    size="16px"
                                />
                            ) : (
                                'Déplacer vers le dossier'
                            )}
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    };

export default MoveFormalitiesToOtherDossierForm;
